/* globals showModalConfirm */
import React, { useContext, useEffect, useState } from "react";
import {
  parseManagementInterface,
  composeApplyCommand,
  areChangesDisruptive,
} from "./api";
import SettingsView from "common/SettingsView";
import ArrangeInColumns from "common/layouts/ArrangeInColumns";
import InterfaceAndVLANSettings from "./InterfaceAndVLANSettings";
import IPAndDNSSettings from "./IPAndDNSSettings";
import useProfile from "common/hooks/useProfile";
import styled from "styled-components";
import BqnContext from "common/BqnContext";
import { parseClusterResponse } from "common/api";
import Request from "common/Request";
import ActionsContext from "common/ActionsContext";
import { Header } from "common/Header";
import LinkStateDetailModal from "views/Status/Interfaces/LinkState/Detail";
import Modal from "common/Modal";
import HelpModal from "common/HelpModal";
import Table from "common/graphs/Table/Table";
import { atoiv6, atoiv4 } from "common/utils";

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  padding-top: 5px;
`;

const doesNothing = () => {};

const CANT_PROCEED_ERROR =
  "Cannot proceed while there are pending config changes." +
  " Try again later.";

const clear = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

class RequiresConfirmation extends Error {
  constructor(reason) {
    super();
    this.reason = reason;
  }
}
class UserDeclined extends Error {}

const confirmWarning = (message, content) => {
  const done = new Promise((resolve) =>
    $("#modalConfirm").on("hidden.bs.modal", resolve)
  );
  return new Promise((resolve, reject) =>
    showModalConfirm(
      message,
      content,
      "warning",
      () => done.then(() => resolve(true)),
      () => done.then(() => reject(new UserDeclined(content))),
      "OK",
      "CANCEL"
    )
  );
};

const ManagementInterfaceContextMenu = ({ doLoad = doesNothing }) => (
  <ul className="context-menu header-dropdown m-r--5">
    <li>
      <a
        onClick={doLoad}
        data-toggle="cardloading"
        data-loading-effect="pulse"
        title="Refresh"
      >
        <i className="material-icons">refresh</i>
      </a>
    </li>
  </ul>
);

const ContextMenu = () => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <a
        data-loading-effect="pulse"
        data-toggle="cardloading"
        title="Help"
        onClick={() => actions.send("open-help")}
      >
        <i className="material-icons">help_outline</i>
      </a>
      <a
        onClick={() => actions.send("do-load")}
        data-toggle="cardloading"
        data-loading-effect="pulse"
        title="Refresh"
      >
        <i className="material-icons">refresh</i>
      </a>
    </ul>
  );
};
const checkNothingPending = () =>
  ifCl.run("show config diff").then((response) => {
    if (response.length > 0) {
      throw new Error(CANT_PROCEED_ERROR);
    }
  });

const clearConfigAttempt = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const notifyAndRevert = (error) => {
  if (error instanceof UserDeclined) {
    return { error: null };
  }
  error = error.message === undefined ? new Error(error) : error;
  clearConfigAttempt();
  showModalError("Error", error);
};

const submitCommand = (command) => ifCl.run(command + "\n", /*is batch*/ true);

const mayRequireConfirmation = (warning) => {
  console.error("mayRequireConfirmation", warning);
  if (warning instanceof RequiresConfirmation) {
    return confirmWarning("Do you want to proceed?", warning.reason);
  }
  throw warning;
};

const checkForDisruptiveChanges = (previous, settings) => {
  if (areChangesDisruptive(previous, settings)) {
    const reason =
      "Please be aware that any changes in the management IP address, " +
      "if there are firewall rules that do not include the new IP address, " +
      "can result in the access to the node management being blocked.";
    throw new RequiresConfirmation(reason);
  }
  return true;
};

const checkForEmptyGateway = (previous, { gateway }) => {
  if (gateway === undefined || gateway.length === 0) {
    const reason = "No gateway defined.";
    throw new RequiresConfirmation(reason);
  }
  return true;
};

const doChecksOnBeforeProceed = (originalSettings, settings) => (commands) =>
  Promise.resolve(commands)
    .then(() => checkForEmptyGateway(originalSettings, settings))
    .catch(mayRequireConfirmation)
    .then(() => checkForDisruptiveChanges(originalSettings, settings))
    .catch(mayRequireConfirmation)
    .then(() => commands);

const getColumns = (mode, actions, setBqnEditing) => {
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "BQN",
            numeric: true,
          },
        ]
      : []),
    {
      label: "INTERFACE",
      id: "INTERFACE",
      numeric: false,
      cellClassName: "hyperlink-text",
      onClick: (value) =>
        actions.send(`show-interface-details`, {
          name: value,
          type: "ethernet",
        }),
    },
    {
      label: "VLAN",
      id: "VLAN",
      numeric: false,
    },
    {
      label: "IP-ADDR/MASK",
      id: "IP-ADDR/MASK",
      numeric: false,
    },
    {
      label: "GATEWAY",
      id: "GATEWAY",
      numeric: false,
      parse: (value) => {
        if (!value) {
          return 0;
        }
        return value.includes(":") ? atoiv6(value) : atoiv4(value);
      },
      parseFilter: (value) => value
    },
    {
      label: "DNS",
      id: "DNS",
      numeric: false,
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      sort: false,
      actions: [
        {
          label: "Edit",
          icon: "edit",
          onClick: (row) => setBqnEditing(row["BQN"]),
        },
      ],
    },
  ];
};

const ManagementInterfaceConfig = () => {
  const [request, setRequest] = useState(null);
  const [bqnEditing, setBqnEditing] = useState(null);
  const [showAddButton, setShowAddButton] = useState(false);

  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);

  useEffect(() => (doLoad(), actions.recv("do-load", doLoad)), []);

  const getManagementInterfaceSettings = () =>
    ifCl
      .run("show interface management detail")
      .then(
        (response) =>
          parseClusterResponse(response, parseManagementInterface)[bqnEditing]
      );

  const doLoad = () => {
    setRequest(
      ifCl.run("show interface management detail").then((response) => {
        const data = parseClusterResponse(response, parseManagementInterface);
        const rows = ["BQN INTERFACE VLAN  IP-ADDR/MASK  GATEWAY DNS"];
        for (const [bqn, resp] of Object.entries(data)) {
          rows.push(
            `${bqn} ${resp.interface} ${resp.vlan ? resp.vlan : "n/a"} ${
              resp.ipAddress
            } ${resp.gateway} ${resp.nameserver ? resp.nameserver : "n/a"}`
          );
        }

        setShowAddButton(mode === "standalone" && !data[bqnId]);

        return { response: rows.join("\n") };
      })
    );
  };

  const apply = (settings, originalSettings = {}) =>
    checkNothingPending()
      .then(() =>
        composeApplyCommand(
          originalSettings,
          settings,
          mode === "cluster" ? bqnEditing : null
        )
      )
      .then(doChecksOnBeforeProceed(originalSettings, settings))
      .then(submitCommand)
      .then(() => doLoad())
      .catch(notifyAndRevert);

  return (
    <>
      <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
        <div className="card container-lg">
          <Header
            title="Management Interface Settings"
            contextMenu={<ContextMenu />}
            bqnSelector={true}
          />
          <div className="body flex-panel-column expect-overflow">
            {showAddButton && (
              <button
                type="button"
                className="btn btn-default waves-effect shadow-none"
                onClick={() => setBqnEditing(bqnId)}
              >
                <i className="material-icons">add_circle</i>
                <span>Add management interface...</span>
              </button>
            )}
            <TableContainer>
              <Request during={request}>
                {({ response }) => {
                  const [header, ...rows] = response.trim().split("\n");
                  const headerAttrs = header.trim().split(/\s+/);
                  const newRows = rows.map((row) => {
                    const rowAttrs = row.trim(/\s/).split(/\s+/);
                    const newEntry = rowAttrs.reduce((acc, attr, index) => {
                      acc[headerAttrs[index]] = attr;
                      return acc;
                    }, {});
                    return newEntry;
                  });
                  const filteredRows = newRows.filter((row) => {
                    if (mode === "standalone" || bqnId === "all") {
                      return true;
                    }
                    return parseFloat(row["BQN"]) === parseFloat(bqnId);
                  });

                  return (
                    <Table
                      isSelectable={false}
                      isRowSelectable={false}
                      data={filteredRows}
                      sort={true}
                      columns={getColumns(mode, actions, setBqnEditing)}
                      search={true}
                      csvPrefix="subscriber-group-values"
                      rowsPerPageNr={50}
                    />
                  );
                }}
              </Request>
            </TableContainer>
          </div>
        </div>
      </div>
      <LinkStateDetailModal />
      {bqnEditing !== null && (
        <Modal
          id="interface-edit"
          large={true}
          onClose={() => setBqnEditing(null)}
          noPadding={true}
          content={() => (
            <SettingsView
              className="container-lg"
              load={getManagementInterfaceSettings}
              apply={apply}
              clear={clear}
              ContextMenu={ManagementInterfaceContextMenu}
              title={`${
                mode === "cluster" ? `BQN ${bqnEditing} ` : ""
              }Management interface settings`}
              applyActionClassName="hidden-to-operators"
              wrapIntoForm={false}
              subtitle={
                <small>
                  Press <code>Apply</code> button at the end of the page to set
                  the new configuration values.
                </small>
              }
              bqnSelector={false}
              noMargin={true}
            >
              <ShowManagementInterfaceSettings bqnId={bqnEditing} />
            </SettingsView>
          )}
        />
      )}
      <Help />
    </>
  );
};

const ShowManagementInterfaceSettings = ({
  defaults,
  state,
  onStateUpdate = doesNothing,
  bqnId,
}) => {
  const { isOperator } = useProfile() || {};
  const updateSettings = (update) => {
    onStateUpdate(update);
  };

  return (
    <div className="body">
      <div className="row">
        <ArrangeInColumns alignToTop={true}>
          <div>
            <InterfaceAndVLANSettings
              isOperatorProfile={isOperator}
              onChange={updateSettings}
              defaults={defaults}
              state={state}
              bqnId={bqnId}
            />
          </div>
          <div>
            <IPAndDNSSettings
              isOperatorProfile={isOperator}
              onChange={updateSettings}
              defaults={defaults}
              state={state}
            />
          </div>
        </ArrangeInColumns>
      </div>
    </div>
  );
};

const Help = () => (
  <HelpModal
    title="MANAGEMENT INTERFACE SETTINGS"
    content={() => (
      <>
        <p>Network interfaces details at the time of loading the page.</p>
        <ul>
          <li>
            <b>BQN</b>: BQN where the interface is configured (only in cluster
            mode).
          </li>
          <li>
            <b>INTERFACE</b>: Interface name.
          </li>
          <li>
            <b>VLAN</b>: VLAN assigned.
          </li>
          <li>
            <b>IP-ADDR/MASK</b>: IP address assigned to the interface.
          </li>
          <li>
            <b>GATEWAY</b>: Gateway assigned to the interface.
          </li>
          <li>
            <b>DNS</b>: DNS server assigned to the interface.
          </li>
        </ul>
        <p>
          Click on the <i className="material-icons valign-icon">refresh</i>{" "}
          icon to refresh interface information.
        </p>
        <p>
          Click on the <i className="material-icons valign-icon">edit</i> icon
          to edit an already configured interface.
        </p>
      </>
    )}
  />
);

export default ManagementInterfaceConfig;
